import ImagePlaceholderSvg from "@/src/assets/icons/icon-image.svg";
import colorSet from "@/src/styles/color";
import { lineClamp } from "@/src/styles/truncate";
import typo from "@/src/styles/typography";
import { ReactNode, useState } from "react";
import styled from "styled-components";

interface TaskThumbnailProps {
  imageUrlList: (string | undefined)[];
  title: ReactNode;
  subTitle?: ReactNode;
  remark?: ReactNode;
  onClickImage: (imageUrl: string) => void;
}

function TaskThumbnail({
  title,
  subTitle,
  remark,
  onClickImage,
  imageUrlList,
}: TaskThumbnailProps) {
  const [imageListHoverIndex, setImageListHoverIndex] = useState<number>(0);

  return (
    <TaskInformationItem>
      <TaskTypeInfo>
        <TaskDetailTypeContainer>
          <Title>{title}</Title>
          {subTitle && <Subtitle>{subTitle}</Subtitle>}
          {remark && <Remark>{remark}</Remark>}
        </TaskDetailTypeContainer>

        <TaskImageList>
          {imageUrlList.map((image, imageIdx) => {
            return (
              <TaskImage
                key={imageIdx.toString()}
                $imageUrl={image || ""}
                onMouseOver={() => {
                  if (!image) {
                    return;
                  }
                  setImageListHoverIndex(imageIdx);
                }}
                $isOpacity={imageListHoverIndex !== imageIdx}
                onClick={() => {
                  if (!image) {
                    return;
                  }

                  onClickImage(image);
                }}
              />
            );
          })}
        </TaskImageList>
      </TaskTypeInfo>
      <StyledTaskThumbnail
        $imageUrl={imageUrlList[imageListHoverIndex] || ""}
        onClick={() => {
          if (!imageUrlList[imageListHoverIndex]) {
            return;
          }

          onClickImage(imageUrlList[imageListHoverIndex] || "");
        }}
      />
    </TaskInformationItem>
  );
}

export default TaskThumbnail;

const TaskInformationItem = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 24px;
  border-radius: 16px;
  border: 1px solid ${colorSet.gray9};
`;

const TaskTypeInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-width: 0;
  min-height: 150px;
`;

const TaskDetailTypeContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const Title = styled.div`
  ${typo.b7m};
  color: ${colorSet.gray2};
`;

const Subtitle = styled.div`
  ${typo.b9r};
  color: ${colorSet.gray5};
  word-break: break-word;
`;

const Remark = styled.div`
  ${lineClamp(2)};
  ${typo.b9r};
  color: ${colorSet.gray5};
  word-break: break-all;
`;

const TaskImageList = styled.div`
  display: flex;
  gap: 8px;
  margin-top: auto;
`;

const StyledTaskThumbnail = styled.div<{ $imageUrl: string }>`
  flex: 1;
  min-width: 150px;
  min-height: 150px;
  border-radius: 8px;
  background: ${({ $imageUrl }) =>
      !!$imageUrl ? `url(${$imageUrl})` : `url(${ImagePlaceholderSvg})`}
    no-repeat;
  background-color: ${({ $imageUrl }) =>
    !!$imageUrl ? "none" : `${colorSet.gray11}`};
  background-position: center;
  background-size: ${({ $imageUrl }) => ($imageUrl ? "cover" : "auto")};
  border: 1px solid ${colorSet.gray10};
  cursor: ${({ $imageUrl }) => ($imageUrl ? "pointer" : "not-allowed")};
`;

const TaskImage = styled.div<{ $imageUrl: string; $isOpacity: boolean }>`
  max-width: 44px;
  flex: 1;
  height: 44px;
  border-radius: 8px;
  background: ${({ $imageUrl }) =>
      !!$imageUrl ? `url(${$imageUrl})` : `url(${ImagePlaceholderSvg})`}
    no-repeat;
  background-color: ${({ $imageUrl }) =>
    !!$imageUrl ? "none" : `${colorSet.gray11}`};
  background-position: center;
  background-size: ${({ $imageUrl }) => ($imageUrl ? "contain" : "auto")};
  border: 1px solid ${colorSet.gray10};
  opacity: ${({ $isOpacity, $imageUrl }) =>
    !$imageUrl ? 1 : $isOpacity ? 0.5 : 1};
  cursor: ${({ $imageUrl }) => ($imageUrl ? "pointer" : "not-allowed")};
`;
